import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import { ChainNames } from 'config';
import { PortfolioData } from 'pages/Landing/components/TvlGraph';
import { AuthNonceResponse, IAuthSignUpLoginResponse } from 'apis';

export const INITIAL_AUTH_NONCE_RESPONSE = {
	current: 0,
	timestamp: '',
	authMessage: '',
};

export const INITIAL_LOGIN_RESPONSE = {
	address: '',
	token: '',
};

export type AppState = {
	selectedNetwork: ChainNames;
	isDrawerOpen: boolean;
	isChatOpen: boolean;
	loader: {
		loading: boolean;
		message: string;
	};
	isWalletOpen: boolean;
	terms: { isOpen: boolean; isAccepted: boolean };
	chart: {
		frequency: number;
		value: PortfolioData[];
	};
	drawdown: {
		7: [];
		30: [];
	};
	isAppReady: boolean;
	isRestoring: boolean;
	loginResponse: IAuthSignUpLoginResponse;
	authNonceResponse: AuthNonceResponse;
	history: { today: string[]; yesterday: string[]; lastWeek: string[] };
};

const initialState: AppState = {
	selectedNetwork: 'HypeEvm',
	isDrawerOpen: false,
	isChatOpen: false,
	loader: {
		loading: false,
		message: '',
	},
	isWalletOpen: false,
	terms: {
		isOpen: false,
		isAccepted: localStorage.getItem('termsCondition') !== null,
	},
	chart: {
		frequency: 7,
		value: [],
	},
	drawdown: {
		7: [],
		30: [],
	},
	isAppReady: false,
	isRestoring: true,
	loginResponse: INITIAL_LOGIN_RESPONSE,
	authNonceResponse: INITIAL_AUTH_NONCE_RESPONSE,
	history: {
		today: [],
		yesterday: [],
		lastWeek: [],
	},
};

export const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		setSelectedChain: (
			state: Draft<AppState>,
			action: PayloadAction<ChainNames>,
		) => {
			state.selectedNetwork = action.payload;
		},
		setChartDrawdown: (
			state: Draft<AppState>,
			action: PayloadAction<{ type: 7 | 30; value: [] }>,
		) => {
			state.drawdown[action.payload.type] = action.payload.value;
		},
		setLoading: (
			state: Draft<AppState>,
			action: PayloadAction<{ loading: boolean; message?: string }>,
		) => {
			state.loader = {
				loading: action.payload.loading,
				message: action.payload.message || 'Please Wait...',
			};
		},
		setAccepted: (
			state: Draft<AppState>,
			action: PayloadAction<{ open: boolean; value: boolean }>,
		) => {
			state.terms = {
				isAccepted: action.payload.value,
				isOpen: action.payload.open,
			};
		},
		openDrawer: (
			state: Draft<AppState>,
			action: PayloadAction<boolean>,
		) => {
			state.isDrawerOpen = action.payload;
		},
		openChatDrawer: (
			state: Draft<AppState>,
			action: PayloadAction<boolean>,
		) => {
			state.isChatOpen = action.payload;
		},
		openWallet: (
			state: Draft<AppState>,
			action: PayloadAction<boolean>,
		) => {
			state.isWalletOpen = action.payload;
		},
		setChartFrequency: (
			state: Draft<AppState>,
			action: PayloadAction<number>,
		) => {
			state.chart.frequency = action.payload;
		},
		setChartData: (
			state: Draft<AppState>,
			action: PayloadAction<PortfolioData[]>,
		) => {
			state.chart.value = action.payload;
		},

		setTodayHistory: (
			state: Draft<AppState>,
			action: PayloadAction<string>,
		) => {
			state.history.today = [action.payload];
		},
		setAppReady: (
			state: Draft<AppState>,
			action: PayloadAction<boolean>,
		) => {
			state.isAppReady = action.payload;
		},

		setAppRestoring: (
			state: Draft<AppState>,
			action: PayloadAction<boolean>,
		) => {
			state.isRestoring = action.payload;
		},
		setLogin: (
			state: Draft<AppState>,
			action: PayloadAction<IAuthSignUpLoginResponse>,
		) => {
			state.loginResponse = action.payload;
		},
		setAuthMessage: (
			state: Draft<AppState>,
			action: PayloadAction<AuthNonceResponse>,
		) => {
			state.authNonceResponse = action.payload;
		},
	},
});

export const authActions = authSlice.actions;
const authReducer = authSlice.reducer;

export default authReducer;
